import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { User, Lock, Trash, Check, Warning, ShieldCheck, EnvelopeSimple } from "@phosphor-icons/react";
import { useAuth, formatAuthError } from "../lib/AuthContext";
import { api } from "../lib/api";
import PushHealthCard from "../components/PushHealthCard";

export default function ProfilePage() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();

  if (user === null) {
    return <div className="p-8 text-[15px] text-tac-dim text-center">Loading…</div>;
  }
  if (user === false) {
    return <Navigate to="/login?next=/profile" replace />;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <h1 className="font-display font-black text-2xl text-tac-text">Account</h1>
        <p className="text-[15px] text-tac-dim mt-1">
          Manage your reporter profile, change your password, or delete your account.
        </p>
      </div>

      <div className="bg-tac-surface/60 border border-tac-border rounded-sm p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-full bg-tac-primary/15 border border-tac-primary/40 text-tac-primary flex items-center justify-center text-lg font-bold shrink-0">
            {(user.display_name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-display font-bold text-tac-text truncate" data-testid="profile-name">
                {user.display_name}
              </span>
              {user.verified ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-tac-primary/55 bg-tac-primary/15 text-tac-primary rounded-sm">
                  <ShieldCheck size={9} weight="fill" /> Verified
                </span>
              ) : (
                <span data-testid="profile-pending-badge" className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-tac-border bg-tac-surface text-tac-dim rounded-sm">
                  Pending verification
                </span>
              )}
            </div>
            <div className="text-[15px] text-tac-dim truncate">{user.email}</div>
          </div>
        </div>
        {user.role === "admin" && (
          <button
            data-testid="profile-go-admin"
            onClick={() => navigate("/admin")}
            className="text-[11px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
          >
            Admin console →
          </button>
        )}
      </div>

      {!user.verified && (
        <div
          data-testid="profile-pending-card"
          className="border border-tac-border bg-tac-surface/40 rounded-sm p-4"
        >
          <div className="text-[15px] text-tac-dim leading-relaxed mb-3">
            <strong className="text-tac-text">Pending verification.</strong> Anyone with an
            account can post field updates — but only verified reporters carry the gold
            badge. Apply if you have ties to fire ops, EM, scanner monitoring, ham radio,
            or are a long-time local with strong situational awareness.
          </div>
          {user.reporter_application ? (
            <button
              data-testid="profile-application-status"
              onClick={() => navigate("/apply-reporter")}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider border border-tac-warning/55 bg-tac-warning/10 text-tac-warning rounded-sm hover:bg-tac-warning/20 transition-colors"
            >
              <ShieldCheck size={12} weight="fill" /> Application under review →
            </button>
          ) : (
            <button
              data-testid="profile-apply-cta"
              onClick={() => navigate("/apply-reporter")}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-tac-primary hover:bg-tac-primary/90 text-white rounded-sm transition-colors"
            >
              <ShieldCheck size={12} weight="fill" /> Apply to be a Verified Reporter
            </button>
          )}
        </div>
      )}

      <PushHealthCard />

      <DisplayNameCard user={user} onUpdated={refresh} />
      <EmailPrefsCard />
      <PasswordCard />
      <DangerZoneCard onDeleted={async () => { await logout(); navigate("/"); }} />
    </div>
  );
}

function DisplayNameCard({ user, onUpdated }) {
  const [name, setName] = useState(user.display_name);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === user.display_name) return;
    setSaving(true);
    setMsg(null);
    try {
      await api.patch("/auth/me", { display_name: name.trim() });
      await onUpdated();
      setMsg({ tone: "ok", text: "Display name updated." });
    } catch (err) {
      setMsg({ tone: "err", text: formatAuthError(err.response?.data?.detail) || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-tac-surface/40 border border-tac-border rounded-sm p-5">
      <h2 className="font-display font-bold text-sm uppercase tracking-wider text-tac-text mb-3 flex items-center gap-2">
        <User size={14} weight="bold" /> Display name
      </h2>
      <input
        data-testid="profile-name-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full bg-tac-bg border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-sm text-tac-text outline-none mb-3"
      />
      <div className="flex items-center justify-between gap-3">
        <CardMsg msg={msg} />
        <button
          type="submit"
          data-testid="profile-name-save"
          disabled={saving || !name.trim() || name.trim() === user.display_name}
          className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider bg-tac-primary hover:bg-tac-primary/90 disabled:opacity-40 text-white rounded-sm transition-colors"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

function EmailPrefsCard() {
  const [on, setOn] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get("/me/email-prefs").then(({ data }) => setOn(data.weekly_email)).catch(() => setOn(true));
  }, []);

  const toggle = async () => {
    const next = !on;
    setOn(next);
    setMsg(null);
    try {
      await api.post("/me/email-prefs", { weekly_email: next });
      setMsg({ tone: "ok", text: next ? "You're subscribed to the weekly update." : "Unsubscribed from the weekly update." });
    } catch (err) {
      setOn(!next);
      setMsg({ tone: "err", text: "Could not save — try again." });
    }
  };

  return (
    <div className="bg-tac-surface/40 border border-tac-border rounded-sm p-5">
      <h2 className="font-display font-bold text-sm uppercase tracking-wider text-tac-text mb-3 flex items-center gap-2">
        <EnvelopeSimple size={14} weight="bold" /> Email preferences
      </h2>
      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <span className="text-sm text-tac-text">
          Weekly Firewatch update
          <span className="block text-xs text-tac-dim">Fires, evacuation levels, lightning and air quality — every Monday morning</span>
        </span>
        <button
          type="button"
          data-testid="profile-weekly-email-toggle"
          onClick={toggle}
          disabled={on === null}
          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${on ? "bg-tac-primary" : "bg-tac-border"}`}
          aria-pressed={!!on}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </label>
      <div className="mt-2"><CardMsg msg={msg} /></div>
    </div>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setCurrent("");
      setNext("");
      setMsg({ tone: "ok", text: "Password changed." });
    } catch (err) {
      setMsg({ tone: "err", text: formatAuthError(err.response?.data?.detail) || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-tac-surface/40 border border-tac-border rounded-sm p-5">
      <h2 className="font-display font-bold text-sm uppercase tracking-wider text-tac-text mb-3 flex items-center gap-2">
        <Lock size={14} weight="bold" /> Change password
      </h2>
      <div className="space-y-3 mb-3">
        <input
          data-testid="profile-current-pw"
          type="password"
          autoComplete="current-password"
          placeholder="Current password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
          className="w-full bg-tac-bg border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-sm text-tac-text outline-none"
        />
        <input
          data-testid="profile-new-pw"
          type="password"
          autoComplete="new-password"
          placeholder="New password (min 8 chars)"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          required
          minLength={8}
          className="w-full bg-tac-bg border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-sm text-tac-text outline-none"
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <CardMsg msg={msg} />
        <button
          type="submit"
          data-testid="profile-pw-save"
          disabled={saving || !current || !next || next.length < 8}
          className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider bg-tac-primary hover:bg-tac-primary/90 disabled:opacity-40 text-white rounded-sm transition-colors"
        >
          {saving ? "Updating…" : "Update password"}
        </button>
      </div>
    </form>
  );
}

function DangerZoneCard({ onDeleted }) {
  const [pw, setPw] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState(null);

  const remove = async () => {
    setDeleting(true);
    setMsg(null);
    try {
      await api.delete("/auth/me", { data: { password: pw } });
      await onDeleted();
    } catch (err) {
      setMsg({ tone: "err", text: formatAuthError(err.response?.data?.detail) || err.message });
      setDeleting(false);
    }
  };

  return (
    <div className="bg-tac-danger/5 border border-tac-danger/40 rounded-sm p-5">
      <h2 className="font-display font-bold text-sm uppercase tracking-wider text-tac-danger mb-2 flex items-center gap-2">
        <Trash size={14} weight="bold" /> Delete account
      </h2>
      <p className="text-[15px] text-tac-dim mb-3 leading-relaxed">
        This permanently removes your account, saved locations, push subscriptions, and
        any field updates you've posted. <strong className="text-tac-text">This cannot be undone.</strong>
      </p>
      {!confirming ? (
        <button
          type="button"
          data-testid="profile-delete-start"
          onClick={() => setConfirming(true)}
          className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider border border-tac-danger bg-tac-danger/10 hover:bg-tac-danger/20 text-tac-danger rounded-sm transition-colors"
        >
          Delete my account…
        </button>
      ) : (
        <div className="space-y-3">
          <input
            data-testid="profile-delete-pw"
            type="password"
            autoComplete="current-password"
            placeholder="Confirm with your password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="w-full bg-tac-bg border border-tac-danger/55 focus:border-tac-danger rounded-sm px-3 py-2 text-sm text-tac-text outline-none"
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardMsg msg={msg} />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setConfirming(false); setPw(""); setMsg(null); }}
                disabled={deleting}
                className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 text-tac-text rounded-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="profile-delete-confirm"
                onClick={remove}
                disabled={deleting || !pw}
                className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider bg-tac-danger hover:bg-tac-danger/90 disabled:opacity-40 text-white rounded-sm"
              >
                {deleting ? "Deleting…" : "Delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CardMsg({ msg }) {
  if (!msg) return <span />;
  const cls = msg.tone === "ok" ? "text-tac-primary" : "text-tac-danger";
  const Icon = msg.tone === "ok" ? Check : Warning;
  return (
    <span data-testid="profile-card-msg" className={`text-xs flex items-center gap-1.5 ${cls}`}>
      <Icon size={12} weight="fill" /> {msg.text}
    </span>
  );
}
